import type { MentionTarget } from "@serophin/shared";
import { useId } from "preact/hooks";
import { Markdown } from "./Markdown";
import { MentionSuggestions } from "./MentionSuggestions";
import type { MarkdownEditorTheme } from "./markdownEditorTheme";
import type { EditorMode } from "./markdownEditorToolbar";
import type { useMentionPopup } from "./useMentionPopup";

type EditorSurfaceProps = {
  mode: EditorMode;
  value: string;
  theme: MarkdownEditorTheme;
  editorId?: string;
  ariaLabel?: string;
  errorId?: string;
  placeholder?: string;
  rows?: number;
  minRows?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: string;
  mentions?: MentionTarget[];
  setTextareaNode: (node: HTMLTextAreaElement | null) => void;
  onInput: (event: Event) => void;
  onKeyDown: (event: KeyboardEvent) => void;
  onKeyUp: (event: KeyboardEvent) => void;
  onClick: () => void;
};

export const EditorSurface = ({
  mode,
  value,
  theme,
  editorId,
  ariaLabel,
  errorId,
  placeholder,
  rows,
  minRows,
  autoFocus,
  disabled,
  error,
  mentions,
  setTextareaNode,
  onInput,
  onKeyDown,
  onKeyUp,
  onClick,
}: EditorSurfaceProps) => {
  if (mode === "preview") {
    return (
      <div class={`w-full min-h-[6rem] px-3 py-2 ${theme.preview}`}>
        {value ? <Markdown source={value} mentions={mentions} skin={theme.markdown} /> : <p class={theme.previewEmpty}>Nothing to preview</p>}
      </div>
    );
  }
  return (
    <textarea
      ref={setTextareaNode}
      id={editorId}
      value={value}
      aria-label={ariaLabel}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? errorId : undefined}
      placeholder={placeholder}
      rows={rows ?? minRows ?? 6}
      autoFocus={autoFocus}
      disabled={disabled}
      onInput={onInput}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onClick={onClick}
      class={`
        w-full px-3 py-2 border resize-none
        focus:outline-none focus:ring-2 disabled:cursor-not-allowed
        ${theme.field} ${theme.fieldFocus}
        ${error ? theme.fieldErrorBorder : theme.fieldBorder}
      `.trim()}
    />
  );
};

export const MentionPopupOverlay = ({ popup, theme }: { popup: ReturnType<typeof useMentionPopup>; theme: MarkdownEditorTheme }) => {
  if (!popup.mentionState || !popup.style) {
    return null;
  }
  return (
    <MentionSuggestions
      targets={popup.candidates}
      theme={theme}
      highlightedIndex={popup.mentionState.highlightedIndex}
      onHighlight={popup.setHighlightedIndex}
      onSelect={popup.insertMention}
      style={popup.style}
      containerRef={(node) => {
        popup.popupRef.current = node;
      }}
    />
  );
};

export const LivePreview = ({ value, theme, mentions }: { value: string; theme: MarkdownEditorTheme; mentions?: MentionTarget[] }) => {
  const labelId = useId();
  return (
    <div role="group" aria-labelledby={labelId} class={`mt-2 px-3 py-2 ${theme.livePreview}`}>
      <p id={labelId} class={`mb-1 ${theme.livePreviewLabel}`}>
        Preview
      </p>
      {value.trim() ? (
        <Markdown source={value} mentions={mentions} variant="compact" skin={theme.markdown} />
      ) : (
        <p class={theme.previewEmpty}>Nothing to preview</p>
      )}
    </div>
  );
};
